// Slice 10:    initial sticky-bottom auto-scroll for the chat message list.
// Slice 10.1:  synchronous setIsStuckBoth ref mirror.
// Slice 10.2:  callback ref so listeners actually bind on mount.
// Slice 10.3:  pill-click + wheel-down recovery fix.
//
// What 10.3 fixes:
//
// (a) Clicking the "↓ Jump to latest" pill cleared showJumpPill but the
//     scrollTop write inside jumpToBottom never took effect. Reproduced
//     live by sampling scrollTop for 20 frames after pill.click() — it
//     stayed at 0 the entire window. The repro showed two related
//     misbehaviours: (i) externally setting scrollTop near-bottom snapped
//     back to 0 if any wheel event had fired earlier in the session, and
//     (ii) wheel-up then wheel-down couldn't reach the bottom by mouse
//     wheel — the hook treated EVERY wheel as "user wants to detach,"
//     never as "user wants to re-engage."
//
// (b) Slice 10.2's onScroll handler called setIsStuckBoth(false) +
//     setShowJumpPill(true) on EVERY scroll where distFromBottom > 120.
//     That made onScroll an active state-flipper, which fought against
//     jumpToBottom's own scrollTo: the synchronous scroll event from
//     scrollTo would re-enter onScroll while the new state was still in
//     flight, and React batching could land the wrong final state. The
//     isProgrammaticScrollRef guard helped but didn't eliminate the
//     window cleanly.
//
// 10.3 design:
//   - userDetachedRef: explicit "user has scrolled up and wants to stay
//     up" flag, separate from isStuck. Set by upward user intent,
//     cleared by re-engagement.
//   - onScroll is read-only for detach: it can re-engage on near-bottom
//     and confirm pill visibility when already detached, but it does NOT
//     newly detach. Detach happens only through user-intent handlers.
//   - onUserIntent branches on e.deltaY sign:
//       deltaY < 0 (up)   AND distFromBottom > 40  → detach
//       deltaY > 0 (down) AND distFromBottom < 120 → re-engage
//   - jumpToBottom clears userDetached + isProgrammatic guard BEFORE
//     calling scrollTo, then engages sticky after — so the synchronous
//     scroll event from scrollTo can't be misread as user intent.
//   - jumpToBottom defers one frame if elRef.current is null (StrictMode
//     double-mount window) so a click that races the ref attach still
//     succeeds on the next frame.
//
// Hook return is unchanged for callers — same exports, plus
// setIsStuckBoth is now also returned for App.jsx force-restick paths.
import { useCallback, useRef, useState } from "react";

const NEAR_BOTTOM_PX     = 120;  // wheel-down recovery threshold
const NEAR_BOTTOM_TIGHT  = 40;   // any-input recovery threshold (End key, scrollbar drag, native scroll)
const DETACH_DEADZONE_PX = 40;   // wheel-up only detaches if we're already > this far from bottom

export function useStickyBottom() {
  // The live element. Read by every handler. Written by setContainer (the
  // callback ref) on mount / unmount. This is the single canonical
  // storage — jumpToBottom and stickToBottom MUST read from this same
  // ref, not from a parallel one, or pill clicks no-op.
  const elRef = useRef(null);
  const [isStuck, setIsStuck] = useState(true);
  const [showJumpPill, setShowJumpPill] = useState(false);

  // Synchronous mirrors so handlers always read the freshest value.
  const isStuckRef = useRef(true);
  // "User has scrolled up and wants to stay up." Independent from isStuck
  // because we want a recovery path that brings the user back near
  // bottom WITHOUT them having to click the pill — and we can't tell
  // those scenarios apart from isStuck alone.
  const userDetachedRef = useRef(false);
  // True around our own scrollTop / scrollTo writes so the synchronous
  // scroll event we trigger doesn't re-enter the user-intent path.
  const isProgrammaticScrollRef = useRef(false);
  const rafRef = useRef(0);

  // Canonical state setter. Updates React state, the synchronous mirror,
  // and the pill all at once. The (stuck === true) branch also clears
  // the user-detach flag — re-engagement always means we're back in
  // tracking mode.
  const setIsStuckBoth = useCallback((stuck) => {
    isStuckRef.current = !!stuck;
    setIsStuck(!!stuck);
    setShowJumpPill(!stuck);
    if (stuck) userDetachedRef.current = false;
  }, []);

  // ── Per-token sticky write (called from the SSE flush) ───────────────
  // No-op while detached. RAF-coalesced so a 50-chunk burst in 16 ms
  // produces one scroll write per frame, not 50.
  const stickToBottom = useCallback(() => {
    const el = elRef.current;
    if (!el || !isStuckRef.current) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      isProgrammaticScrollRef.current = true;
      el.scrollTop = el.scrollHeight;
      requestAnimationFrame(() => { isProgrammaticScrollRef.current = false; });
    });
  }, []);

  // ── User clicked the pill / new-prompt-send wants to force-stick ────
  // Reads the live element from the same ref the callback ref writes
  // into. If for any reason the ref isn't attached yet (StrictMode
  // double-mount, suspense boundary, etc.) we defer one frame and try
  // again — silent failure on first click is the actual symptom 10.3
  // is fixing.
  const jumpToBottom = useCallback(() => {
    const doJump = (el) => {
      // Order matters. Clear userDetached + arm the programmatic guard
      // BEFORE the scroll write so the synchronous scroll event we
      // trigger lands inside the guarded window and can't be misread as
      // upward user intent.
      userDetachedRef.current = false;
      isProgrammaticScrollRef.current = true;
      // scrollTo with behavior:"instant" matches the explicit-jump
      // semantics — no smooth animation that would queue and fight
      // subsequent token streams. Falls back to scrollTop assignment on
      // the few engines that ignore the option.
      try {
        el.scrollTo({ top: el.scrollHeight, behavior: "instant" });
      } catch (_) {
        el.scrollTop = el.scrollHeight;
      }
      setIsStuckBoth(true);
      requestAnimationFrame(() => { isProgrammaticScrollRef.current = false; });
    };
    const el = elRef.current;
    if (el) { doJump(el); return; }
    // Defer one frame. If the ref still isn't attached after that,
    // silently bail — there's nothing reasonable to do.
    requestAnimationFrame(() => {
      const el2 = elRef.current;
      if (el2) doJump(el2);
    });
  }, [setIsStuckBoth]);

  // ── Scroll handler: read-only for detach, recovery on near-bottom ───
  // Critically: this handler does NOT newly detach. Detach happens only
  // through onUserIntent (wheel/touch with upward direction). That
  // separation means the synchronous scroll event from jumpToBottom's
  // scrollTo can't accidentally undo the engagement.
  const onScroll = useCallback(() => {
    const el = elRef.current;
    if (!el) return;
    if (isProgrammaticScrollRef.current) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distFromBottom < NEAR_BOTTOM_TIGHT) {
      // Recovery: any time the user lands within 40 px of the bottom
      // (End key, scrollbar drag, wheel-down, momentum scroll), clear
      // detach and resume sticky on the next stream token.
      userDetachedRef.current = false;
      setIsStuckBoth(true);
      return;
    }
    if (userDetachedRef.current) {
      // Already detached and still scrolled away — keep the pill visible.
      // (Don't call setIsStuckBoth(false) every scroll; it'd churn React
      // state on every scroll-pixel and lose the chance to set
      // userDetached cleanly.)
      if (isStuckRef.current) {
        isStuckRef.current = false;
        setIsStuck(false);
      }
      setShowJumpPill(true);
    }
    // !userDetached && !nearBottom → don't change anything. This range
    // is "auto-stick is chasing a stream" — the per-token
    // stickToBottom() owns scroll position, not us.
  }, [setIsStuckBoth]);

  // ── User-intent handler: only path that newly detaches ──────────────
  // wheel-up far from bottom → detach.
  // wheel-down near bottom    → re-engage.
  // touchmove → fall back to position-only via the scroll handler;
  //             no direction info in a single touchmove without
  //             touchstart bookkeeping.
  const onUserIntent = useCallback((e) => {
    const el = elRef.current;
    if (!el) return;
    if (isProgrammaticScrollRef.current) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const dy = (typeof e.deltaY === "number") ? e.deltaY : 0;

    // Wheel DOWN into the near-bottom zone — user wants sticky back.
    if (dy > 0 && distFromBottom < NEAR_BOTTOM_PX) {
      userDetachedRef.current = false;
      setIsStuckBoth(true);
      return;
    }
    // Wheel UP from a position that isn't already glued to bottom —
    // user wants to detach.
    if (dy < 0 && distFromBottom > DETACH_DEADZONE_PX) {
      userDetachedRef.current = true;
      isStuckRef.current = false;
      setIsStuck(false);
      setShowJumpPill(true);
      return;
    }
    // touchmove or wheel without a clear direction signal — leave it to
    // onScroll to react based on final position.
  }, [setIsStuckBoth]);

  // ── Callback ref. Binds listeners synchronously the moment the DOM
  //    node mounts (10.2's reason for switching off useEffect) and
  //    cleans them up when it unmounts. setContainer's identity is
  //    stable across renders because all three callbacks it closes over
  //    have stable deps.
  const setContainer = useCallback((el) => {
    const prev = elRef.current;
    if (prev && prev !== el) {
      prev.removeEventListener("scroll",    onScroll);
      prev.removeEventListener("wheel",     onUserIntent);
      prev.removeEventListener("touchmove", onUserIntent);
    }
    elRef.current = el;
    if (el) {
      el.addEventListener("scroll",    onScroll,    { passive: true });
      el.addEventListener("wheel",     onUserIntent, { passive: true });
      el.addEventListener("touchmove", onUserIntent, { passive: true });
    }
  }, [onScroll, onUserIntent]);

  return {
    containerRef: setContainer,
    isStuck,
    showJumpPill,
    stickToBottom,
    jumpToBottom,
    setIsStuckBoth,
  };
}
